import { Card, Icon } from '@ics-portal/guardian';
import clsx from 'clsx';
import { type ReactNode } from 'react';
import { useProjectSummary } from '@pages/project/_utils/contexts/ProjectSummaryContext';
import { getProjectPath, ProjectPath } from '@utils/getMenuContent';
import { simplePluralize } from '@utils/stringFormatUtils';
import { useSummaryCards } from '@pages/project/summary/_utils/contexts/SummaryCardsContext';
import { SummaryCardContent } from './components/SummaryCardContent';
import { SummaryCardTitle } from './components/SummaryCardTitle';
import s from './SummaryCards.module.scss';

function splitPhaseName(input: string): string[] {
    const substrings = input.split(/(>=|<=|>|<)/);
    return substrings.filter((s) => s !== '');
}

function getTasksHeader(phaseName: string): ReactNode {
    const splitted = splitPhaseName(phaseName);
    const symbol = splitted.length === 3 ? splitted[2] : null;
    const description = splitted[0];
    const symbolClassName = clsx('text-white', s.projectSymbol);
    return (
        <div className="flex align-items-center">
            {symbol ? <h2 className={symbolClassName}>{symbol}</h2> : null}
            {symbol ? (
                <span className="text-white">{description}</span>
            ) : (
                <h2 className="text-white">{description}</h2>
            )}
        </div>
    );
}

export interface ProjectPhasesCardProps {
    pid: string;
}

export function ProjectMilestonesCard(
    props: ProjectPhasesCardProps,
): ReactNode {
    const { pid } = props;
    const { project } = useProjectSummary();
    const { data } = useSummaryCards();

    const tasksTotal = data?.tasksCount ?? 0;
    const tasksCompleted = data?.tasksCompletedCount ?? 0;
    const allTasksCompleted = tasksTotal > 0 && tasksTotal === tasksCompleted;

    const tasksHeader = getTasksHeader(project?.milestone?.name ?? '');
    const tasksDescription = (
        <div className="text-white">
            {allTasksCompleted ? 'All tasks are completed' : null}
            {!allTasksCompleted ? (
                <>
                    Project completeness {tasksCompleted}/{tasksTotal}{' '}
                    {simplePluralize('task', tasksTotal)}
                </>
            ) : null}
        </div>
    );
    const tasksFooter = (
        <div className="flex text-white align-items-center gap-2">
            {allTasksCompleted ? (
                <>
                    <Icon name="check_circle" className={s.successIcon} />{' '}
                    Completed
                </>
            ) : null}
            {!allTasksCompleted ? (
                <>
                    <Icon name="chronic" className={s.statusIcon} /> In progress
                </>
            ) : null}
        </div>
    );

    return (
        <Card
            title={
                <SummaryCardTitle
                    icon="dashboard"
                    title="Project Guidance"
                    iconClassName={s.dashboardIcon}
                />
            }
            cardClassName={s.card}
            foldable={false}
        >
            <SummaryCardContent
                content={
                    <div className="flex flex-column gap-2">
                        {tasksHeader}
                        {tasksDescription}
                    </div>
                }
                footer={tasksFooter}
                footerClassName="mt-2"
                to={getProjectPath(ProjectPath.Guidance, pid)}
            />
        </Card>
    );
}
